import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
  Grid,
  Button,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SpeedIcon from '@mui/icons-material/Speed';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Can from '../../components/Can';
import SyncIcon from '@mui/icons-material/Sync';
import BlockIcon from '@mui/icons-material/Block';
import notaCreditoVentaService, { NotaCreditoVenta } from '../../services/notaCreditoVentaService';
import PDFViewer from '../../components/shared/PDFViewer';
import AplicarSaldoReciboDialog from '../../components/Tesoreria/AplicarSaldoReciboDialog';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  esDocumentoVentaAnulado,
  puedeMostrarAnularNotaCreditoVenta,
} from '../../utils/ventasDocumentoAnulacion';
import { VerAsientoContableMenuItem } from '../../components/shared/VerAsientoContableButton';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';
import { useAuth } from '../../contexts/AuthContext';
import { formatClienteConNro } from '../../utils/formatters';

interface NotasCreditoLocationState {
  aplicarNc?: NotaCreditoVenta;
}

function isSuperAdmin(roles: string[] | undefined): boolean {
  return (roles ?? []).some((r) => String(r).toUpperCase() === 'SUPERADMIN');
}

const NotasCredito: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user?.roles);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { enqueueSnackbar } = useSnackbar();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();

  const [notasCredito, setNotasCredito] = useState<NotaCreditoVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedNotaCredito, setSelectedNotaCredito] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [obteniendoCae, setObteniendoCae] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; notaCreditoId: number } | null>(null);
  const [generandoAsiento, setGenerandoAsiento] = useState(false);
  const [openGenerarAsientoDialog, setOpenGenerarAsientoDialog] = useState<number | null>(null);
  const [aplicarDialogOpen, setAplicarDialogOpen] = useState(false);
  const [aplicarDialogNc, setAplicarDialogNc] = useState<NotaCreditoVenta | null>(null);

  const fetchNotasCredito = async (
    pageNumber: number,
    pageSize: number,
    search: string,
    desde: string,
    hasta: string,
    nroSuc: number | null,
  ) => {
    try {
      setLoading(true);
      // Asegurar que pageSize esté entre 1 y 20 (límite del backend)
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const response = await notaCreditoVentaService.getNotasCreditoVenta({
        pageNumber: pageNumber,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde || undefined,
        fechaHasta: hasta || undefined,
        nroSuc: resolveNroSucFiltro(nroSuc),
      });

      setNotasCredito(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      console.error('Error al cargar notas de crédito:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al cargar las notas de crédito');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
      setNotasCredito([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!filtroListo) return;
    const timeoutId = setTimeout(() => {
      fetchNotasCredito(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc, filtroListo]);

  useEffect(() => {
    const state = location.state as NotasCreditoLocationState | null;
    const nc = state?.aplicarNc;
    if (!nc?.nroFormulario || !(nc.nroCliente > 0)) return;
    setAplicarDialogNc(nc);
    setAplicarDialogOpen(true);
    navigate('.', { replace: true, state: {} });
  }, [location.state, navigate]);

  const handleAbrirAplicarSaldo = (nc: NotaCreditoVenta) => {
    if (!nc.nroCliente || nc.nroCliente <= 0) {
      enqueueSnackbar('La nota de crédito no tiene cliente asociado.', { variant: 'warning' });
      return;
    }
    setAplicarDialogNc(nc);
    setAplicarDialogOpen(true);
  };

  const handleCloseAplicarDialog = () => {
    setAplicarDialogOpen(false);
    setAplicarDialogNc(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      setPdfLoading(nroFormulario);
      const notaCredito = notasCredito.find((n) => n.nroFormulario === nroFormulario);
      const idCompania = String(notaCredito?.idCompania ?? '').trim() || undefined;
      const blob = await notaCreditoVentaService.getNotaCreditoVentaPDF(nroFormulario, idCompania);
      setPdfFileName(null);
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedNotaCredito(nroFormulario);
    } catch (error: any) {
      console.error('Error al generar el PDF de la nota de crédito:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al generar el PDF de la nota de crédito');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedNotaCredito(null);
    setPdfFileName(null);
  };

  const handleObtenerCae = async (nroFormulario: number) => {
    try {
      setObteniendoCae(nroFormulario);
      const notaCredito = notasCredito.find((n) => n.nroFormulario === nroFormulario);
      const idCompania = String(notaCredito?.idCompania ?? '').trim() || undefined;
      const notaCreditoActualizada = await notaCreditoVentaService.obtenerCaeArgentina(nroFormulario, idCompania);
      
      setNotasCredito(prevNotasCredito => 
        prevNotasCredito.map(n => 
          n.nroFormulario === nroFormulario 
            ? { ...n, idCompania: notaCreditoActualizada.idCompania ?? n.idCompania, caeNro: notaCreditoActualizada.caeNro, caeVencimiento: notaCreditoActualizada.caeVencimiento }
            : n
        )
      );
      
      enqueueSnackbar('CAE obtenido exitosamente', { variant: 'success' });
    } catch (error: any) {
      console.error('Error al obtener CAE:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al obtener el CAE');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 8000
      });
    } finally {
      setObteniendoCae(null);
    }
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, notaCreditoId: number) => {
    event.stopPropagation();
    setKebabAnchor({ el: event.currentTarget, notaCreditoId });
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const esNotaCreditoAnulada = (notaCredito: NotaCreditoVenta) =>
    esDocumentoVentaAnulado(notaCredito.asentado, notaCredito.nombre, true);

  const handleAnular = async (notaCreditoId: number) => {
    if (!window.confirm('¿Está seguro que desea anular esta nota de crédito?')) return;
    try {
      await notaCreditoVentaService.anularNotaCredito(notaCreditoId);
      enqueueSnackbar('Nota de crédito anulada correctamente', { variant: 'success' });
      fetchNotasCredito(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular la nota de crédito'), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const currentKebabNotaCredito = kebabAnchor
    ? notasCredito.find((n) => n.nroFormulario === kebabAnchor.notaCreditoId)
    : null;

  const notaCreditoParaGenerarAsiento = openGenerarAsientoDialog
    ? notasCredito.find((n) => n.nroFormulario === openGenerarAsientoDialog)
    : null;

  const puedeGenerarAsiento = (notaCredito: NotaCreditoVenta) =>
    superAdmin && (notaCredito.importeTotal ?? 0) > 0 && (notaCredito.nroFormulario ?? 0) > 0;

  const ejecutarGenerarAsiento = async () => {
    const id = openGenerarAsientoDialog;
    if (!id) return;
    setOpenGenerarAsientoDialog(null);
    setGenerandoAsiento(true);
    try {
      const r = await notaCreditoVentaService.regenerarAsientoNotaCreditoVenta(id);
      enqueueSnackbar(r.message ?? `Asiento generado (Nº ${r.nroAsiento}).`, { variant: 'success' });
      setNotasCredito((prev) =>
        prev.map((n) => (n.nroFormulario === id ? { ...n, nroAsiento: r.nroAsiento } : n))
      );
      navigate(`/contabilidad/asientos-diario/${r.nroAsiento}`);
    } catch (error: unknown) {
      const ax = error as { response?: { data?: { message?: string } } };
      const msg = ax.response?.data?.message ?? 'No se pudo generar el asiento.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setGenerandoAsiento(false);
    }
  };

  const renderKebabItems = (notaCredito: NotaCreditoVenta) => {
    const items: React.ReactElement[] = [
      <VerAsientoContableMenuItem
        key="asiento"
        nroAsiento={notaCredito.nroAsiento}
        onClose={handleKebabClose}
      />,
    ];

    if (superAdmin) {
      items.push(
        <MenuItem
          key="generar-asiento"
          disabled={!puedeGenerarAsiento(notaCredito) || generandoAsiento}
          onClick={() => {
            handleKebabClose();
            setOpenGenerarAsientoDialog(notaCredito.nroFormulario);
          }}
        >
          <SyncIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
          Generar asiento
        </MenuItem>
      );
    }

    if (puedeMostrarAnularNotaCreditoVenta(notaCredito.caeNro, notaCredito.asentado, notaCredito.nombre)) {
      items.push(
        <MenuItem
          key="anular"
          onClick={() => {
            handleKebabClose();
            void handleAnular(notaCredito.nroFormulario);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <BlockIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Anular nota de crédito</ListItemText>
        </MenuItem>
      );
    }

    return items;
  };
  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '-';
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      return date.toLocaleDateString('es-AR');
    } catch {
      return typeof dateString === 'string' ? dateString : '-';
    }
  };

  const formatComprobante = (notaCredito: NotaCreditoVenta) => {
    const { letra, nroSuc, nroComprobante } = notaCredito;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  /** Notas de crédito internas (letra X): no requieren CAE AFIP (misma regla que facturas X). */
  const esNotaCreditoInterna = (notaCredito: NotaCreditoVenta) => {
    const letra = (notaCredito.letra ?? '').toString().trim().toUpperCase();
    return letra === 'X';
  };

  const getEstadoChip = (notaCredito: NotaCreditoVenta) => {
    if (esNotaCreditoAnulada(notaCredito)) {
      return { label: 'Anulada', bgColor: '#FFCDD2', textColor: '#C62828' };
    }
    if (esNotaCreditoInterna(notaCredito)) {
      return { label: 'Interno', bgColor: '#ECEFF1', textColor: '#455A64' };
    }
    if (notaCredito.caeNro) {
      return { label: 'Con CAE', bgColor: '#C8E6C9', textColor: '#2E7D32' };
    }
    return { label: 'Sin CAE', bgColor: '#FFE0B2', textColor: '#E65100' };
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Notas de Crédito de Venta
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Can permiso="ventas.notas-credito.nuevo">
              <Button
                variant="contained"
                color="primary"
                startIcon={<SpeedIcon />}
                onClick={() => navigate('/ventas/notas-credito/nuevo-pos')}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                Nueva Nota de Crédito POS
              </Button>
              </Can>
            </Stack>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, cliente, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {notasCredito.map((notaCredito) => {
                const estadoChip = getEstadoChip(notaCredito);
                const comprobante = formatComprobante(notaCredito);
                return (
                  <Card 
                    key={notaCredito.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {comprobante !== '-' ? comprobante : 'Nota de Crédito'}
                            </Typography>
                          </Box>
                          <Chip
                            label={estadoChip.label}
                            size="small"
                            sx={{
                              bgcolor: estadoChip.bgColor,
                              color: estadoChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {formatDate(notaCredito.fechaEmision)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cliente:</strong> {formatClienteConNro(notaCredito.nroCliente, notaCredito.nombre)}
                        </Typography>
                        {notaCredito.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {notaCredito.cuit.trim()}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe Total:</strong> {formatCurrency(notaCredito.importeTotal)}
                        </Typography>
                        {notaCredito.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {notaCredito.usuario}
                          </Typography>
                        )}
                        {notaCredito.caeNro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {notaCredito.caeNro}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                          {!notaCredito.caeNro && !esNotaCreditoInterna(notaCredito) && !esNotaCreditoAnulada(notaCredito) && (
                            <Tooltip title="Obtener CAE de Argentina">
                              <IconButton
                                onClick={() => handleObtenerCae(notaCredito.nroFormulario)}
                                size="small"
                                disabled={obteniendoCae === notaCredito.nroFormulario}
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#1976d2' },
                                  '&:disabled': { bgcolor: '#ccc' }
                                }}
                              >
                                {obteniendoCae === notaCredito.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <CloudDownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(notaCredito.nroFormulario)}
                              size="small"
                              disabled={pdfLoading === notaCredito.nroFormulario}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' },
                                '&:disabled': { bgcolor: '#ccc' }
                              }}
                            >
                              {pdfLoading === notaCredito.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          {notaCredito.nroCliente > 0 && !esNotaCreditoAnulada(notaCredito) && (
                            <Tooltip title="Aplicar saldo / Ver aplicaciones">
                              <IconButton
                                size="small"
                                onClick={() => handleAbrirAplicarSaldo(notaCredito)}
                                sx={{
                                  bgcolor: '#2e7d32',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#1b5e20' },
                                }}
                              >
                                <AccountBalanceWalletIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Más acciones">
                            <IconButton
                              size="small"
                              onClick={(e) => handleKebabOpen(e, notaCredito.nroFormulario)}
                              sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && notasCredito.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay notas de crédito para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  width: '100%'
                }
              }}
            >
          <Table 
            size={isTablet ? 'small' : 'medium'} 
            sx={{ 
              minWidth: 900,
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.75rem', md: '0.875rem', lg: '0.875rem' },
                padding: { xs: '8px 4px', sm: '10px 6px', md: '12px 8px' }
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 90 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' }, minWidth: 160 }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 150, maxWidth: 280 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>CUIT</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 120 }} align="right">Importe Total</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' }, minWidth: 80 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' }, minWidth: 100 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 120 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notasCredito.map((notaCredito) => {
                const estadoChip = getEstadoChip(notaCredito);
                return (
                  <TableRow 
                    key={notaCredito.nroFormulario} 
                    sx={{
                      '&:hover': {
                        bgcolor: '#f9f9f9',
                      },
                    }}
                  >
                    <TableCell>{formatDate(notaCredito.fechaEmision)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                      {formatComprobante(notaCredito)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatClienteConNro(notaCredito.nroCliente, notaCredito.nombre)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{notaCredito.cuit?.trim() || '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1976d2' }} align="right">{formatCurrency(notaCredito.importeTotal)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{notaCredito.usuario || '-'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                      <Chip
                        label={estadoChip.label}
                        size="small"
                        sx={{
                          bgcolor: estadoChip.bgColor,
                          color: estadoChip.textColor,
                          fontWeight: 500,
                          borderRadius: 1.5,
                          minWidth: 90,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                        {!notaCredito.caeNro && !esNotaCreditoInterna(notaCredito) && !esNotaCreditoAnulada(notaCredito) && (
                          <Tooltip title="Obtener CAE de Argentina">
                            <IconButton
                              onClick={() => handleObtenerCae(notaCredito.nroFormulario)}
                              size="small"
                              disabled={obteniendoCae === notaCredito.nroFormulario}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#1976d2',
                                  transform: 'scale(1.1)',
                                },
                                '&:disabled': { bgcolor: '#ccc' },
                                transition: 'all 0.2s'
                              }}
                            >
                              {obteniendoCae === notaCredito.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <CloudDownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Imprimir PDF">
                          <IconButton
                            onClick={() => handleImprimirPDF(notaCredito.nroFormulario)}
                            size="small"
                            disabled={pdfLoading === notaCredito.nroFormulario}
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              width: 32,
                              height: 32,
                              '&:hover': { 
                                bgcolor: '#1976d2',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s',
                              '&:disabled': { bgcolor: '#ccc' }
                            }}
                          >
                            {pdfLoading === notaCredito.nroFormulario ? (
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                            ) : (
                              <PrintIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        {notaCredito.nroCliente > 0 && !esNotaCreditoAnulada(notaCredito) && (
                          <Tooltip title="Aplicar saldo / Ver aplicaciones">
                            <IconButton
                              size="small"
                              onClick={() => handleAbrirAplicarSaldo(notaCredito)}
                              sx={{
                                bgcolor: '#2e7d32',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  bgcolor: '#1b5e20',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s',
                              }}
                            >
                              <AccountBalanceWalletIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Más acciones">
                          <IconButton
                            size="small"
                            onClick={(e) => handleKebabOpen(e, notaCredito.nroFormulario)}
                            sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
              {loading && notasCredito.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && notasCredito.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay notas de crédito para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1
                  }
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedNotaCredito && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Nota de Crédito ${selectedNotaCredito}`}
          documentId={`${selectedNotaCredito}`}
          documentType="nota-credito"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      <Menu
        anchorEl={kebabAnchor?.el}
        open={Boolean(kebabAnchor)}
        onClose={handleKebabClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {currentKebabNotaCredito && renderKebabItems(currentKebabNotaCredito)}
      </Menu>

      <Dialog
        open={openGenerarAsientoDialog !== null}
        onClose={() => !generandoAsiento && setOpenGenerarAsientoDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generar asiento contable</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            {notaCreditoParaGenerarAsiento
              ? `Se eliminarán todas las líneas del libro diario vinculadas a la nota de crédito ${formatComprobante(notaCreditoParaGenerarAsiento)} (incluido si el asiento estaba contabilizado) y se creará un asiento nuevo con los importes y cuentas según la configuración actual, incluyendo el costo de mercadería. ¿Continuar?`
              : 'Se eliminarán todas las líneas del libro diario vinculadas a esta nota de crédito y se creará un asiento nuevo con la lógica actual. ¿Continuar?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGenerarAsientoDialog(null)} disabled={generandoAsiento}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={ejecutarGenerarAsiento} disabled={generandoAsiento}>
            {generandoAsiento ? 'Procesando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {aplicarDialogNc && aplicarDialogNc.nroCliente > 0 && (
        <AplicarSaldoReciboDialog
          open={aplicarDialogOpen}
          onClose={handleCloseAplicarDialog}
          reciboId={aplicarDialogNc.nroFormulario}
          nroCliente={aplicarDialogNc.nroCliente}
          nombreCliente={aplicarDialogNc.nombre || `Cliente ${aplicarDialogNc.nroCliente}`}
          numeroComprobanteCredito={
            formatComprobante(aplicarDialogNc) !== '-'
              ? formatComprobante(aplicarDialogNc)
              : undefined
          }
          nroTipoFormularioCredito={aplicarDialogNc.nroTipoFormulario}
          importeTotalCredito={aplicarDialogNc.importeTotal}
          onSuccess={() =>
            fetchNotasCredito(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc)
          }
        />
      )}
    </Box>
  );
};

export default NotasCredito;
